import React, { useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import useStyles from "./useStyles";
import { hasElements } from "utils/arrays";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HelpIcon from "@material-ui/icons/Help";
import PeopleAltOutlined from "@material-ui/icons/PeopleAltOutlined";
import DescriptionOutlined from "@material-ui/icons/DescriptionOutlined";
import LanguageOutlined from "@material-ui/icons/LanguageOutlined";
import HomeWorkOutlined from "@material-ui/icons/HomeWorkOutlined";
import DateRangeOutlined from "@material-ui/icons/DateRangeOutlined";
import CircleIcon from "@material-ui/icons/FiberManualRecordSharp";
import EditIcon from "@material-ui/icons/EditOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/CloseOutlined";
import EventOutlined from "@material-ui/icons/EventOutlined";
import NotesOutlinedIcon from "@material-ui/icons/NotesOutlined";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import { getDefaultEventColor } from "./utils";
import { Paper, IconButton, Button, Grid, TextField } from "@material-ui/core";
import { getEventFields } from "entities/moduleUtils";
import AppContext from "AppContext";
import { dateToDayOfMonth, dateToDayOfWeekName, dateToMonthName, timeToString } from "utils/dateUtils";
import CalendarContext from "./CalendarContext";
import RichTextEditorHTML from "components/common_components/RichTextEditor/RichTextEditorHTML";

function EventView({
    data,
    onEdit,
    onDelete,
    onClose,
    onGoing,
    onNotify,
    isMine,
    canEditEvent,
    canDeleteEvent,
    canSeeGuestList,
    allowGoingAnswer = true,
}) {
    const classes = useStyles();
    const DEFAULT_EVENT_COLOR = getDefaultEventColor();
    const EVENT_COLOR = data.eventColor || DEFAULT_EVENT_COLOR;
    const { allowedModules } = useContext(AppContext);
    const { user, resources, getEventCalendarName } = useContext(CalendarContext);
    const history = useHistory();
    const quillRef = useRef();

    const [note, setNote] = useState("");
    const [mouseOverGuestId, setMouseOverGuestId] = useState(undefined);

    const getUserNameByEmail = (email) => {
        if (resources && hasElements(resources.users)) {
            const user = resources.users.find((x) => x.email === email);
            return user ? user.name : email;
        }
        return email;
    };

    const isGuestGoing = (guest) => {
        if (guest.going === null) {
            return { Icon: HelpIcon, color: "silver" };
        }
        return guest.going ? { Icon: CheckCircleIcon, color: "green" } : { Icon: CancelIcon, color: "red" };
    };

    const buildEventDateString = (start, end, allDay) => {
        const monthName = dateToMonthName(start);
        const day = dateToDayOfMonth(start);
        const dayName = dateToDayOfWeekName(start);
        if (allDay) {
            return `${dayName}, ${monthName} ${day}  .  All day`;
        }
        const startTime = timeToString(start);
        const endTime = timeToString(end);
        return `${dayName}, ${monthName} ${day}  .  ${startTime} - ${endTime}`;
    };

    const onEditHandler = () => onEdit(data);
    const onDeleteHandler = () => onDelete(data);
    const onGoingClickHandler = (answer) => {
        const calendarGuest = data.guests.find((x) => x.guestEmail === user.email);
        if (calendarGuest && calendarGuest.id) {
            onGoing(calendarGuest.id, answer, note.trim());
        }
    };
    const onNoteChangeHandler = (e) => setNote(e.target.value);
    const onMouseOverGuestHandler = setMouseOverGuestId;
    const onNotifyGuestHandler = (guestId) => {
        if (isMine) {
            onNotify(guestId);
        }
    };

    const hasFileCaseModuleInfo = () => Boolean(data && data.moduleCode && data.fileCaseId && data.dependsOnField);

    const onClickModuleHandler = () => {
        const modulePath = allowedModules.find((x) => x.code === data.moduleCode)?.path;
        history.push(modulePath, { fileCaseId: data.fileCaseId });
    };

    const getFieldName = () => {
        return getEventFields(data.moduleCode).find((x) => x.field === data.dependsOnField)?.label;
    };

    return (
        data && (
            <Paper className={classes.paper} variant="elevation">
                <Grid container spacing={2}>
                    <Grid item xs={12} className={classes.buttons}>
                        <IconButton color="default" component="span" onClick={onClose} title="Close">
                            <CloseIcon color="action" />
                        </IconButton>
                        {canDeleteEvent && (
                            <IconButton color="default" component="span" onClick={onDeleteHandler} title="Delete">
                                <DeleteIcon color="action" />
                            </IconButton>
                        )}
                        {canEditEvent && (
                            <IconButton color="default" component="span" onClick={onEditHandler} title="Edit">
                                <EditIcon color="action" />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={1} className={classes.flexAlignItemsCenter}>
                        <CircleIcon htmlColor={EVENT_COLOR} />
                    </Grid>
                    <Grid item xs={11}>
                        <h1>{data.title}</h1>
                        {hasFileCaseModuleInfo() && (
                            <small>
                                <a href="#top" onClick={onClickModuleHandler}>{`Linked to ${getFieldName()}`}</a>
                            </small>
                        )}
                    </Grid>
                    <Grid item xs={1}>
                        <DateRangeOutlined color="action" />
                    </Grid>
                    <Grid item xs={11}>
                        {buildEventDateString(data.start, data.end, data.allDay)}
                    </Grid>
                    {data.placeType !== "none" && data.place && (
                        <>
                            <Grid item xs={1}>
                                {data.placeType === "virtual" ? <LanguageOutlined color="action" /> : <HomeWorkOutlined color="action" />}
                            </Grid>
                            <Grid item xs={11}>
                                {data.placeType === "virtual" ? (
                                    <a href={data.place} target="_blank" rel="noopener noreferrer">
                                        {data.place}
                                    </a>
                                ) : (
                                    data.place
                                )}
                            </Grid>
                        </>
                    )}
                    {data.description && (
                        <>
                            <Grid item xs={1}>
                                <DescriptionOutlined color="action" />
                            </Grid>
                            <Grid item xs={11}>
                                <RichTextEditorHTML readOnly defaultValue={data.description} ref={quillRef} />
                            </Grid>
                        </>
                    )}
                    {canSeeGuestList && (
                        <>
                            <Grid item xs={1}>
                                <PeopleAltOutlined color="action" />
                            </Grid>
                            <Grid item xs={11} onMouseLeave={() => onMouseOverGuestHandler(undefined)}>
                                {hasElements(data.guests) &&
                                    data.guests.map((guest) => {
                                        const name = getUserNameByEmail(guest.guestEmail);
                                        const isExternal = name === guest.guestEmail;
                                        const IsGoing = isGuestGoing(guest);
                                        const isOrganizer = data.ownerEmail === guest.guestEmail;
                                        const isMouseOver = mouseOverGuestId === guest.id;
                                        const extraStyles = isMouseOver
                                            ? { backgroundColor: `${EVENT_COLOR}0a`, borderRadius: "5px", cursor: "default" }
                                            : {};

                                        return (
                                            <Grid key={`readonly-guest-${guest.id}`} item xs={12}>
                                                <div
                                                    className={classes.readOnlyGuest}
                                                    style={extraStyles}
                                                    onMouseOver={() => onMouseOverGuestHandler(guest.id)}
                                                >
                                                    <div className={classes.autocompleteOptionUserName}>
                                                        <div className={classes.flexAlignItemsCenter}>
                                                            <strong>{name}</strong>
                                                            {isMouseOver && isMine && (
                                                                <IconButton
                                                                    size="small"
                                                                    component="span"
                                                                    onClick={() => onNotifyGuestHandler(guest.id)}
                                                                    title="Send reminder"
                                                                >
                                                                    <NotificationsActiveIcon fontSize="small" htmlColor="#D50000" />
                                                                </IconButton>
                                                            )}
                                                        </div>
                                                        <div className={classes.flexAlignItemsCenter}>
                                                            {isExternal && <small className={classes.guestSmallInfo}>Outside</small>}
                                                            {isOrganizer && <small className={classes.guestSmallInfo}>Organizer</small>}
                                                            {guest.note && <NotesOutlinedIcon className={classes.isGoingIcon} />}
                                                            <IsGoing.Icon className={classes.isGoingIcon} htmlColor={IsGoing.color} />
                                                        </div>
                                                    </div>
                                                    <div className={classes.autocompleteOptionUserName}>
                                                        <div>
                                                            {!isExternal && (
                                                                <small className={classes.guestSmallInfo}>{guest.guestEmail}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={classes.autocompleteOptionUserName}>
                                                        <div>
                                                            {guest.note && <small className={classes.guestSmallInfo}>{guest.note}</small>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Grid>
                                        );
                                    })}
                            </Grid>
                        </>
                    )}
                    <Grid item xs={1}>
                        <EventOutlined color="primary" />
                    </Grid>
                    <Grid item xs={11}>
                        <strong>{getEventCalendarName(data, "view")}</strong>
                    </Grid>

                    {allowGoingAnswer && (
                        <>
                            <Grid item xs={12} className={classes.goingNote}>
                                <TextField
                                    label=""
                                    placeholder="Answer with a short note"
                                    multiline
                                    minRows={2}
                                    value={note}
                                    onChange={onNoteChangeHandler}
                                />
                            </Grid>
                            <Grid item xs={12} className={classes.goingActions}>
                                <span>Going?</span>
                                <div>
                                    <Button color="primary" variant="outlined" size="small" onClick={() => onGoingClickHandler(true)}>
                                        Yes
                                    </Button>
                                    <Button size="small" onClick={() => onGoingClickHandler(false)}>
                                        No
                                    </Button>
                                    <Button size="small" onClick={() => onGoingClickHandler(null)}>
                                        Maybe
                                    </Button>
                                </div>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
        )
    );
}

export default EventView;
